import { useState } from 'react';
import { brandLogoUrl } from '@/entities/car/brandLogo';
import { BrandMark } from './Brand';

/**
 * The make's emblem on a light plate — the thing that tells two rows of «Мой парк» apart at a
 * glance. Before it existed every row carried the same generic glyph and distinguished nothing.
 *
 * Ratios come from Android's `CarBrandLogo` and are proportional to `size`, not absolute:
 * corner 0.27, inner padding 0.10, and our own mark at 0.62 when the make is unknown. The
 * padding is deliberately tight — half the logos are wide wordmarks (Haval, Exeed, Bestune) and
 * fitted into a square they already come out half the plate's height.
 *
 * **The light plate belongs to the emblem, not to the row.** It exists because half the factory
 * logos are black on transparent (Toyota, Porsche, Mercedes-Benz) and vanish on a dark ground,
 * and the other half arrive with an opaque white rectangle baked in (Omoda, Maybach, Lada) and
 * would sit in a bright square anyway. Under our own mark there is no such file to protect, and
 * forcing the plate light there — with the mark pinned to a dark ink so it would still show —
 * punched a white square into a dark list. So that one case follows the theme: the plate takes
 * the card's own surface, and `BrandMark` inverts with it, which is what its colours are tokens
 * for.
 */
export function CarBrandLogo({ brand, size = 40 }: { brand: string; size?: number }) {
  const url = brandLogoUrl(brand);
  // A file can go missing between a rename and a deploy; the mark is a better answer than the
  // browser's broken-image icon.
  const [failed, setFailed] = useState(false);

  if (url === null || failed) {
    return (
      <span
        className="grid shrink-0 place-items-center overflow-hidden bg-surface-alt"
        style={{
          width: size,
          height: size,
          borderRadius: size * 0.27,
          border: '1px solid var(--color-line)',
        }}
      >
        <BrandMark size={size * 0.62} />
      </span>
    );
  }

  return (
    <span
      className="grid shrink-0 place-items-center overflow-hidden"
      style={{
        width: size,
        height: size,
        borderRadius: size * 0.27,
        background: '#ffffff',
        border: '1px solid #e4e2dd',
      }}
    >
      <img
        src={url}
        alt=""
        loading="lazy"
        decoding="async"
        onError={() => setFailed(true)}
        style={{ width: size, height: size, padding: size * 0.1, objectFit: 'contain' }}
      />
    </span>
  );
}
